









































































import { FieldType } from '@getaccept/lib-shared/src/fields/types/enums/field-type';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import { TooltipPlacement } from '@getaccept/lib-shared/src/enums/tooltip-placement';
import type { Field as IField } from '@getaccept/lib-shared/src/fields/types/field';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { FieldSource } from '@getaccept/lib-shared/src/fields/types/enums/field-source';
import { FieldHelper } from '@getaccept/lib-shared/src/fields/helpers/field.helper';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { PropType } from '@vue/composition-api';
import { defineComponent, watch, computed, ref } from '@vue/composition-api';
import SignatureField from './SignatureField.vue';
import InitialsField from './InitialsField.vue';
import CheckboxField from './CheckboxField.vue';
import DropdownField from './DropdownField.vue';
import MergeField from './MergeField.vue';
import LinkField from './LinkField.vue';
import TextField from './TextField.vue';

export default defineComponent({
  components: {
    InitialsField,
    CheckboxField,
    MergeField,
    LinkField,
    TextField,
    DropdownField,
    SignatureField,
  },
  props: {
    field: {
      type: Object as PropType<IField>,
      required: true,
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: () => ({} as Recipient),
    },
    allRecipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      default: () => ({}),
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
    },
    requiredStepsLeft: {
      type: Boolean,
    },
    isSigned: {
      type: Boolean,
    },
    initials: {
      type: String,
      default: '',
    },
    verifiedInitialFieldIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const active = ref(false);
    const focus = ref(false);
    const pressSpace = ref(false);

    const watchFocusedRequiredId = (focusedRequiredId: string) => {
      if (props.field.id === focusedRequiredId) {
        setFocus(true);
      } else {
        setFocus(false);
      }
    };

    watch(() => props.focusedRequiredId, watchFocusedRequiredId);

    const errorMessage = computed(
      () => props.field.validationWarning || t('invalid-field-value-entered')
    );

    const tabIndex = computed(() => {
      if (props.isDisabled || !FieldHelper.isTabbableField(props.field.type)) {
        return undefined;
      }
      return 0;
    });

    const fontSize = computed(() => `${(props.field.position.height * 0.6).toFixed(1)}px`);

    const top = computed(() => `${props.field.position.top}px`);

    const left = computed(() => `${props.field.position.left}px`);

    const height = computed(() => {
      let fieldHeight = `${props.field.position.height}px`;
      if (!shouldGrow.value || !props.field.value) {
        return fieldHeight;
      }
      if (!isConnectedToCurrentRecipient.value) {
        fieldHeight = 'auto';
      }
      return fieldHeight;
    });

    const width = computed(() => {
      if (props.field.type === FieldType.Initials) {
        return 'auto';
      }
      return `${props.field.position.width}px`;
    });

    const minWidth = computed(() => {
      if (props.field.type === FieldType.Initials) {
        return `${props.field.position.width}px`;
      }
      return 'auto';
    });

    const fieldType = computed(() => `${props.field.type}-field`);

    const fieldSource = computed(() =>
      props.field.source === FieldSource.Sender ? 'sender-field' : 'recipient-field'
    );

    const pageType = computed(() => 'signing-page-field');

    const completed = computed(() =>
      FieldHelper.fieldCompleted(props.field, props.verifiedInitialFieldIds)
    );

    const disabled = computed(() => {
      if (props.isDisabled) {
        return true;
      }

      if (
        props.field.source === FieldSource.Sender ||
        (props.recipient &&
          (props.recipient.status === RecipientStatus.Signed ||
            props.recipient.status === RecipientStatus.Rejected))
      ) {
        return true;
      }
      return (
        FieldHelper.isInputField(props.field.type) && props.field.recipientId !== props.recipient.id
      );
    });

    const signatureDisabled = computed(
      () => props.field.type === FieldType.Signature && !disabled.value && props.requiredStepsLeft
    );

    const tooltip = computed(() => {
      if (showTooltip.value) {
        return {
          content: tooltipContent.value,
          offset: 10,
          placement: TooltipPlacement.Top,
        };
      }
      return null;
    });

    const showTooltip = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return false;
      }
      return FieldHelper.showFieldTooltip(
        props.field.type,
        disabled.value,
        props.requiredStepsLeft
      );
    });

    const invalid = computed(() => {
      if (props.field.type !== FieldType.Formatted) {
        return false;
      }
      return props.field.value && !new RegExp(props.field.validationRegex).test(props.field.value);
    });

    const tooltipContent = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return t('field-assigned-to-you');
      }
      if (signatureDisabled.value) {
        return t('required-steps-remaining-before-sign');
      }
      return `${t('field-assigned-to')} ${fullName.value}`;
    });

    const fullName = computed(() => {
      switch (props.field.source) {
        case FieldSource.Recipient:
          return !!fieldRecipient.value && fieldRecipient.value.fullName;
        case FieldSource.Sender:
          return !!props.sender && props.sender.fullName;
        default:
          return null;
      }
    });

    const fieldRecipient = computed(() => {
      if (!props.field || !props.field.recipientId) {
        return null;
      }
      return props.allRecipients.find(
        (recipient: Recipient) => recipient.id === props.field.recipientId
      );
    });

    const shouldGrow = computed(() =>
      [FieldType.Text, FieldType.Formatted, FieldType.Merge].includes(props.field.type)
    );

    const isTextBasedField = computed(
      () => props.field.type === FieldType.Text || props.field.type === FieldType.Formatted
    );

    const isConnectedToCurrentRecipient = computed(
      () => props.recipient?.id === props.field.recipientId
    );

    const handleInput = (value: string) => {
      emit('input', value);
    };

    const handleDropdownToggle = (isOpen: boolean) => {
      active.value = isOpen;
    };

    const handleUpdate = (value: string) => {
      emit('update', { fieldId: props.field.id, value });
    };

    const setFocus = (hasFocus: boolean) => {
      if (disabled.value) {
        return;
      }
      focus.value = hasFocus;
    };

    const setPressSpace = (event: KeyboardEvent, press: boolean) => {
      if (props.field.type === FieldType.Check) {
        event.preventDefault();
        pressSpace.value = press;
      }
    };

    const handleInitialsClick = () => {
      if (disabled.value) {
        return;
      }
      emit('clickInitialsField', props.field.id);
    };

    const handleClickLink = () => {
      if (props.field.value) {
        window.open(
          props.field.value.includes('http') ? props.field.value : `//${props.field.value}`
        );
      }
      emit('clickLinkField', props.field.id);
    };

    const handleSignature = () => {
      emit('clickSignField');
    };

    return {
      FieldType,
      FieldSource,
      RecipientStatus,
      active,
      completed,
      disabled,
      height,
      fieldSource,
      fieldRecipient,
      fieldType,
      focus,
      fullName,
      handleDropdownToggle,
      handleInitialsClick,
      handleClickLink,
      handleInput,
      handleSignature,
      handleUpdate,
      invalid,
      isTextBasedField,
      isConnectedToCurrentRecipient,
      minWidth,
      pageType,
      pressSpace,
      setFocus,
      setPressSpace,
      shouldGrow,
      signatureDisabled,
      tooltip,
      tooltipContent,
      width,
      errorMessage,
      tabIndex,
      fontSize,
      top,
      left,
      watchFocusedRequiredId,
    };
  },
});
